// A measure's scope versions and options on the device: finding the one being
// priced, making the first one, and saving what the desk changes. They sync
// through the outbox like every other record (version before option).
import { removeOptionApi } from './api';
import { forgetRecord, getRecord, patchFields, saveRecord, type DeviceRecord, type Fields } from './device-db';
import { copiedOptionFields, versionOptions } from './option-list';
import { emptyScope, parseScope, serialiseScope, type OptionScope } from '@higgins/m2q-pricing';
import { syncNow } from './outbox';
import type { OptionColumns, OptionKind } from '@higgins/m2q-pricing';
import type { PricedOption } from '@higgins/m2q-pricing';

const FIRST_OPTION_NAME = 'A – Repaint';
const PERCENT = 100;
const CENTS = 100;

const num = (value: unknown): number | null => (typeof value === 'number' ? value : null);

/** The measure's latest version that has options, with them in order; null until one is made. */
export function currentVersion(records: DeviceRecord[], measureId: string): { version: DeviceRecord; options: DeviceRecord[] } | null {
  const versions = records
    .filter((r) => r.table === 'version' && r.parentId === measureId)
    .sort((a, b) => (num(b.fields.hig_versionnumber) ?? 0) - (num(a.fields.hig_versionnumber) ?? 0));
  for (const version of versions) {
    const options = versionOptions(records, version.id);
    if (options.length) return { version, options };
  }
  return null;
}

/** A new option in the version, copying `from`'s choices (estimators price a variant of what they have). Returns its id. */
export async function addOption(version: DeviceRecord, from: DeviceRecord, options: DeviceRecord[], kind?: OptionKind): Promise<string> {
  const id = crypto.randomUUID();
  // The copy is of the option as stored now, so a change still being saved from the screen isn't missed.
  const stored = (await getRecord(from.id)) ?? from;
  await saveRecord({ id, table: 'option', jobId: version.jobId, parentId: version.id, etag: null, fields: copiedOptionFields(stored, options, kind) });
  void syncNow();
  return id;
}

/** Measures this app session has made a first version for — opening the screen twice (or before the device re-reads) mustn't make two. */
const started = new Set<string>();

/**
 * Make version 1 (a Draft priced at `date`) and its option "A – Repaint" for a
 * measure that has none. The option starts empty: every value from the measure
 * and the settings until the estimator changes one.
 */
export async function makeFirstOption(measure: DeviceRecord, date: string): Promise<void> {
  if (started.has(measure.id)) return;
  started.add(measure.id);
  try {
    const versionId = crypto.randomUUID();
    await saveRecord({
      id: versionId,
      table: 'version',
      jobId: measure.jobId,
      parentId: measure.id,
      etag: null,
      fields: { hig_name: 'v1', hig_versionnumber: 1, hig_status: 'Draft', hig_pricingdate: date },
    });
    await saveRecord({
      id: crypto.randomUUID(),
      table: 'option',
      jobId: measure.jobId,
      parentId: versionId,
      etag: null,
      fields: { hig_name: FIRST_OPTION_NAME, hig_kind: 'Repaint', hig_labourtype: (measure.fields.hig_labourtype as string | null) ?? null, hig_sortorder: 0, hig_scope: serialiseScope(emptyScope()) },
    });
    void syncNow();
  } catch (err) {
    started.delete(measure.id);
    throw err;
  }
}

/** Whether an option can be recommended yet: the version's lookup can only point at an option CRM already has. */
export const canRecommend = (option: DeviceRecord): boolean => option.etag !== null;

/**
 * Make `chosen` the version's recommended option: its hig_recommended on, any
 * other option's off, and the version's lookup pointed at it — kept in step,
 * so CRM lists show the recommended option's figures (SCHEMA.md).
 */
export async function recommendOption(version: DeviceRecord, options: DeviceRecord[], chosen: DeviceRecord): Promise<void> {
  if (!canRecommend(chosen)) return;
  for (const option of options) {
    const wanted = option.id === chosen.id;
    if ((option.fields.hig_recommended === true) !== wanted) await patchFields(option.id, { hig_recommended: wanted });
  }
  if (version.fields.hig_recommendedoptionid !== chosen.id) await patchFields(version.id, { hig_recommendedoptionid: chosen.id });
  void syncNow();
}

/** Remove an option in CRM (the server refuses the accepted, recommended or only one), then from the device. */
export async function removeOption(option: DeviceRecord): Promise<void> {
  await removeOptionApi(option.id);
  await forgetRecord(option.id);
}

/** The option's columns as the pricing inputs take them. */
export function optionColumns(option: DeviceRecord): OptionColumns {
  return {
    labourType: (option.fields.hig_labourtype as string | null) ?? null,
    markupMaterials: num(option.fields.hig_markupmaterials),
    markupOther: num(option.fields.hig_markupother),
    negotiatingFactor: num(option.fields.hig_negotiatingfactor),
    scope: parseScope(option.fields.hig_scope as string | null),
  };
}

/**
 * Change the option's choices. The change is applied to the choices stored on the device now, not
 * the ones a screen last drew, so edits in quick succession (typing, Tab to the next box) all land.
 */
export async function saveScope(option: DeviceRecord, change: (scope: OptionScope) => OptionScope): Promise<void> {
  await patchFields(option.id, (stored) => ({ hig_scope: serialiseScope(change(parseScope(stored.hig_scope as string | null))) }));
  void syncNow();
}

/** Save fields of a record; `fields` may be worked out from the stored ones (see patchFields). */
export async function saveFields(record: DeviceRecord, fields: Fields | ((stored: Fields) => Fields)): Promise<void> {
  await patchFields(record.id, fields);
  void syncNow();
}

const round = (value: number) => Math.round(value * CENTS) / CENTS;

/**
 * The totals CRM lists show (sell, cost, GP %, hours, litres), when they differ
 * from what the option holds; null when they already match, so opening the
 * screen doesn't queue a save.
 */
export function totalsToSave(option: DeviceRecord, priced: PricedOption): Fields | null {
  const totals: Fields = {
    hig_totalsell: round(priced.sell),
    hig_totalcost: round(priced.cost),
    hig_gppercent: round(priced.gp * PERCENT),
    hig_totalhours: round(priced.hours),
    hig_totallitres: round(priced.litres),
  };
  const same = Object.entries(totals).every(([key, value]) => num(option.fields[key]) === value);
  return same ? null : totals;
}
